import React, { useRef, useState, useEffect } from "react";
import "./ReelsPlayer.css";

const ReelsPlayer = ({
  videoUrl,
  autoPlay = true,
  muted = true,
  controls = true,
  loop = true,

}) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(muted);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Video event handlers
  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleMuteUnmute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
      setVolume(newVolume);
      if (newVolume === 0) {
        setIsMuted(true);
      } else if (isMuted) {
        setIsMuted(false);
      }
    }
  };

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      setIsLoading(false);
    }
  };

  const handleSeek = (e) => {
    if (videoRef.current) {
      const seekTime = parseFloat(e.target.value);
      videoRef.current.currentTime = seekTime;
      setCurrentTime(seekTime);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
  };

  const handleError = () => {
    setHasError(true);
    setIsLoading(false);
  };

  const handleLoadStart = () => {
    setIsLoading(true);
    setHasError(false);
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
  };

  // Auto-play when component mounts
  useEffect(() => {
    if (videoRef.current && autoPlay) {
      const playVideo = async () => {
        try {
          await videoRef.current.play();
          setIsPlaying(true);
        } catch (error) {
          console.log("Auto-play failed:", error);
          setIsPlaying(false);
        }
      };
      playVideo();
    }
  }, [autoPlay]);

  // full screen videos
  const handleFullscreen = () => {
    const video = videoRef.current;

    if (!video) return;

    if (video.requestFullscreen) {
      video.requestFullscreen();
    } else if (video.webkitRequestFullscreen) {
      video.webkitRequestFullscreen();
    } else if (video.msRequestFullscreen) {
      video.msRequestFullscreen();
    }
  };

  // Reset when videoUrl changes
  useEffect(() => {
    setIsLoading(true);
    setHasError(false);
    setCurrentTime(0);
    setIsPlaying(false);
  }, [videoUrl]);

  if (!videoUrl) {
    return (
      <div className="reels-player-container error">
        <div className="error-message">
          <i className="fas fa-video-slash"></i>
          <p>No video URL provided</p>
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="reels-player-container error">
        <div className="error-message">
          <i className="fas fa-exclamation-triangle"></i>
          <p>Failed to load video</p>
          {/* <button
            className="retry-button"
            onClick={() => window.location.reload()}
          >
            Retry
          </button> */}
        </div>
      </div>
    );
  }

  return (
    <div className="reels-player-container">
      {/* Video Element */}
      <video
        ref={videoRef}
        className="reels-video"
        src={videoUrl}
        muted={isMuted}
        loop={loop}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onError={handleError}
        onLoadStart={handleLoadStart}
        playsInline
        preload="metadata"
        //  onClick={handlePlayPause}
      />

      {/* Loading Spinner */}
      {isLoading && (
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading video...</p>
        </div>
      )}

      {/* Custom Controls */}
      {controls && (
        <div className="video-controls"
          onClick={(e) => e.stopPropagation()}>
          {/* Progress Bar */}
          <div className="progress-container">
            <input
              type="range"
              className="progress-bar"
              min="0"
              max={duration || 0}
              value={currentTime}
                onClick={(e) => e.stopPropagation()}
              onChange={handleSeek}
            />
            <div className="time-display">
              <span>{formatTime(currentTime)}</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          {/* Control Buttons */}
          <div className="control-buttons">
            <button
              className="control-btn play-pause-btn"
              // onClick={handlePlayPause}
              onClick={(e) => {
                e.stopPropagation();
                handlePlayPause();
              }}
              aria-label={isPlaying ? "Pause" : "Play"}
            >
              <i className={`fas fa-${isPlaying ? "pause" : "play"}`}></i>
            </button>

            <button
              className="control-btn mute-btn"
              // onClick={handleMuteUnmute}
              onClick={(e) => {
                e.stopPropagation();
                handleMuteUnmute();
              }}
              aria-label={isMuted ? "Unmute" : "Mute"}
            >
              <i className={`fas fa-volume-${isMuted ? "mute" : "up"}`}></i>
            </button>

            {/* Volume Slider */}
            <div className="volume-container">
              <input
                type="range"
                className="volume-slider"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                  onClick={(e) => e.stopPropagation()}
                onChange={handleVolumeChange}
              />
            </div>

            {/* Fullscreen Button */}
            <button
              className="control-btn fullscreen-btn"
              // onClick={() => videoRef.current?.requestFullscreen()}
              onClick={(e) => {
                e.stopPropagation();
                handleFullscreen();
              }}
              aria-label="Fullscreen"
            >
              <i className="fas fa-expand"></i>
            </button>
          </div>
        </div>
      )}

      {/* Play/Pause Overlay Button */}
      {!isLoading && (
        // <div className="video-overlay" onClick={handlePlayPause}>
        //   <div
        //     className={`play-pause-overlay ${isPlaying ? "playing" : "paused"}`}
        //   >
        //     <i className={`fas fa-${isPlaying ? "pause" : "play"}`}></i>
        //   </div>
        // </div>
        <div className="video-overlay">
  <div
    className={`play-pause-overlay ${isPlaying ? "playing" : "paused"}`}
    onClick={handlePlayPause}
  >
    <i className={`fas fa-${isPlaying ? "pause" : "play"}`}></i>
  </div>
</div>
      )}
    </div>
  );
};

export default ReelsPlayer;
